import type { ConnectionState, LogEntry } from '../types';
import { createFakeLog } from './fakeLogs';
import { extractLogsFromPayload, normalizeIncomingLog } from './logParser';

const FALLBACK_INTERVAL_MS = 420;
const TELEMETRY_POLL_INTERVAL_MS = 4500;
const RECONNECT_INITIAL_MS = 1200;
const RECONNECT_MAX_MS = 10_000;
const SEEN_LOG_BUFFER_SIZE = 3000;

const ORG_SLUG_PATTERN = /^[a-z0-9](?:[a-z0-9-]{1,30}[a-z0-9])$/;

const resolveDevWsUrl = (): string => {
  const fromVite = import.meta.env.VITE_WS_URL;
  if (typeof fromVite === 'string' && fromVite.length > 0) {
    return fromVite;
  }

  const maybeProcess = (globalThis as { process?: { env?: Record<string, string | undefined> } }).process;
  const fromProcess = maybeProcess?.env?.VITE_WS_URL;
  if (typeof fromProcess === 'string' && fromProcess.length > 0) {
    return fromProcess;
  }

  return 'ws://localhost:8080/logs';
};

const resolveTelemetryOrigin = (input?: string | null): string => {
  const candidate =
    input ??
    (typeof import.meta.env.VITE_TELEMETRY_ORIGIN === 'string' ? import.meta.env.VITE_TELEMETRY_ORIGIN : null) ??
    'https://telemetry.w7s.io';

  return candidate.trim().replace(/\/+$/, '');
};

const telemetryWsUrl = (telemetryOrigin: string, orgSlug: string): string => {
  const base = telemetryOrigin.replace(/^http:/, 'ws:').replace(/^https:/, 'wss:');
  return `${base}/${encodeURIComponent(orgSlug)}/ws`;
};

const telemetryLogsJsonUrl = (telemetryOrigin: string, orgSlug: string): string =>
  `${telemetryOrigin}/${encodeURIComponent(orgSlug)}/logs?format=json&seconds=90&limit=1000`;

interface StreamOptions {
  orgSlug?: string | null;
  githubToken?: string | null;
  telemetryOrigin?: string | null;
  onLog: (log: LogEntry) => void;
  onConnectionChange: (state: ConnectionState) => void;
}

export const startLogStream = ({
  orgSlug,
  githubToken,
  telemetryOrigin,
  onLog,
  onConnectionChange
}: StreamOptions) => {
  const normalizedOrg = (orgSlug ?? '').trim().toLowerCase();
  const hasOrgScope = ORG_SLUG_PATTERN.test(normalizedOrg);
  const trimmedToken = (githubToken ?? '').trim();

  const seenIds: string[] = [];
  const seenIdSet = new Set<string>();

  const emitLog = (entry: LogEntry) => {
    if (seenIdSet.has(entry.id)) {
      return;
    }

    seenIdSet.add(entry.id);
    seenIds.push(entry.id);
    if (seenIds.length > SEEN_LOG_BUFFER_SIZE) {
      const removed = seenIds.shift();
      if (removed) {
        seenIdSet.delete(removed);
      }
    }

    onLog(entry);
  };

  const emitPayload = (payload: unknown) => {
    const logs = extractLogsFromPayload(payload);
    if (logs.length === 0) {
      emitLog(normalizeIncomingLog(payload));
      return;
    }

    logs.forEach(emitLog);
  };

  if (hasOrgScope) {
    const normalizedTelemetryOrigin = resolveTelemetryOrigin(telemetryOrigin);
    const wsUrl = telemetryWsUrl(normalizedTelemetryOrigin, normalizedOrg);
    const logsUrl = telemetryLogsJsonUrl(normalizedTelemetryOrigin, normalizedOrg);

    let disposed = false;
    let ws: WebSocket | null = null;
    let pollTimer: number | null = null;
    let reconnectTimer: number | null = null;
    let reconnectDelayMs = RECONNECT_INITIAL_MS;

    const stopPolling = () => {
      if (pollTimer !== null) {
        window.clearInterval(pollTimer);
        pollTimer = null;
      }
    };

    const clearReconnect = () => {
      if (reconnectTimer !== null) {
        window.clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
    };

    const scheduleReconnect = () => {
      if (disposed || reconnectTimer !== null) {
        return;
      }

      reconnectTimer = window.setTimeout(() => {
        reconnectTimer = null;
        connectWebSocket();
      }, reconnectDelayMs);

      reconnectDelayMs = Math.min(RECONNECT_MAX_MS, Math.floor(reconnectDelayMs * 1.65));
    };

    const pollTelemetryLogs = async () => {
      if (disposed) {
        return;
      }

      const headers = new Headers();
      if (trimmedToken) {
        headers.set('Authorization', `Bearer ${trimmedToken}`);
      }

      try {
        const response = await fetch(logsUrl, {
          method: 'GET',
          credentials: 'include',
          headers
        });

        if (response.status === 401) {
          onConnectionChange('auth_required');
          if (!trimmedToken) {
            stopPolling();
          }
          return;
        }

        if (!response.ok) {
          onConnectionChange('error');
          return;
        }

        const payload = (await response.json()) as unknown;
        emitPayload(payload);
        onConnectionChange('connected');
      } catch {
        onConnectionChange('error');
      }
    };

    const startPolling = () => {
      if (pollTimer !== null) {
        return;
      }

      void pollTelemetryLogs();
      pollTimer = window.setInterval(() => {
        void pollTelemetryLogs();
      }, TELEMETRY_POLL_INTERVAL_MS);
    };

    const connectWebSocket = () => {
      if (disposed) {
        return;
      }

      onConnectionChange('connecting');

      try {
        ws = new WebSocket(wsUrl);
      } catch {
        if (trimmedToken) {
          startPolling();
          scheduleReconnect();
        } else {
          onConnectionChange('auth_required');
        }
        return;
      }

      ws.onopen = () => {
        reconnectDelayMs = RECONNECT_INITIAL_MS;
        onConnectionChange('connected');

        try {
          ws?.send(
            JSON.stringify({
              clientId: `logverse-${Math.random().toString(36).slice(2, 10)}`,
              action: 'subscribe',
              topic: 'all',
              apiToken: trimmedToken || 'default'
            })
          );
        } catch {
          // best effort subscribe frame
        }
      };

      ws.onmessage = (event) => {
        try {
          const parsed = JSON.parse(event.data as string);
          if (parsed && typeof parsed === 'object' && 'value' in (parsed as Record<string, unknown>)) {
            emitPayload((parsed as Record<string, unknown>).value);
            return;
          }

          emitPayload(parsed);
        } catch {
          emitPayload({
            timestamp: Date.now(),
            body: String(event.data),
            severityNumber: 9,
            attributes: {
              'service.name': 'telemetry-ws'
            }
          });
        }
      };

      ws.onerror = () => {
        // close handler drives state transitions/reconnect.
      };

      ws.onclose = () => {
        if (disposed) {
          return;
        }

        if (trimmedToken) {
          startPolling();
          scheduleReconnect();
          return;
        }

        onConnectionChange('auth_required');
      };
    };

    connectWebSocket();

    if (trimmedToken) {
      startPolling();
    } else {
      void pollTelemetryLogs();
    }

    return {
      url: wsUrl,
      dispose: () => {
        disposed = true;
        stopPolling();
        clearReconnect();
        if (ws) {
          ws.close();
        }
      }
    };
  }

  const url = resolveDevWsUrl();
  let fallbackTimer: number | null = null;
  let nativeSocket: WebSocket | null = null;

  const startFallback = () => {
    if (fallbackTimer !== null) {
      return;
    }

    onConnectionChange('fallback');
    fallbackTimer = window.setInterval(() => {
      emitLog(createFakeLog());
    }, FALLBACK_INTERVAL_MS);
  };

  const stopFallback = () => {
    if (fallbackTimer !== null) {
      window.clearInterval(fallbackTimer);
      fallbackTimer = null;
    }
  };

  onConnectionChange('connecting');

  try {
    nativeSocket = new WebSocket(url);
    nativeSocket.onopen = () => {
      stopFallback();
      onConnectionChange('connected');
    };
    nativeSocket.onmessage = (event) => {
      try {
        emitPayload(JSON.parse(event.data));
      } catch {
        emitPayload({
          timestamp: Date.now(),
          body: String(event.data),
          severityNumber: 9,
          attributes: {
            'service.name': 'local-stream'
          }
        });
      }
    };
    nativeSocket.onerror = () => {
      startFallback();
    };
    nativeSocket.onclose = () => {
      startFallback();
    };

    window.setTimeout(() => {
      if (!nativeSocket || nativeSocket.readyState !== WebSocket.OPEN) {
        startFallback();
      }
    }, 3500);
  } catch {
    startFallback();
  }

  return {
    url,
    dispose: () => {
      stopFallback();
      if (nativeSocket) {
        nativeSocket.close();
      }
    }
  };
};
